import PptxGenJS from 'pptxgenjs';
import IConnection from '@app/types/data/base/IConnection';
import ISeason from '@app/types/data/base/ISeason';
import { colors } from '@app/services/export/ppt/variables/constants';
import ISeasonWithPrice from '@app/types/data/base/ISeasonWithPrice';
import { copy, copyBold } from '@app/services/export/ppt/variables/typography';
import SeasonType from '@app/types/base/SeasonType';
import global from '@app/filters/global';
import { Connection } from '@app/services/export/ppt/classes/Connection';
import GlobalsStoreModule from '@app/store/modules/GlobalsStoreModule';
import { getModule as getStoreModule } from 'vuex-module-decorators';
import { AbstractPptComponent, IPptComponentPosition } from '@app/services/export/ppt/classes/AbstractPptComponent';

interface IProps {
	connection: IConnection;
	seasons?: ISeason[] | ISeasonWithPrice[];
}

export class ConnectingTicket extends AbstractPptComponent {
	protected slide: PptxGenJS.Slide;

	protected props: IProps;

	protected readonly headlineHeight = 0.25;

	protected readonly seasonTextHeight = 0.2;

	protected readonly seasonMargin = 0.06;

	protected readonly i18n = getStoreModule(GlobalsStoreModule).i18n;

	protected connection: Connection;

	public constructor(slide: PptxGenJS.Slide, props: IProps, position: IPptComponentPosition) {
		super(position);
		this.slide = slide;
		this.props = props;

		this.connection = new Connection(this.slide, { connection: this.props.connection, maxWidth: this.position.w });
	}

	public create(): void {
		const iconDimension = 0.2;

		const textDefaults = {
			x: this.position.x,
			y: this.position.y,
			w: this.position.w,
			color: colors.blue,
			margin: 0
		};

		// icon
		this.slide.addImage({
			path: 'assets/images/icons/svg/connecting-ticket-dash.svg',
			x: this.position.x,
			y: this.position.y + 0.03,
			w: 28,
			h: 16,
			sizing: {
				type: 'contain',
				w: iconDimension,
				h: iconDimension
			}
		});

		// headline
		this.slide.addText(this.i18n.connectingTicket, {
			...copyBold,
			...textDefaults,
			h: this.headlineHeight,
			x: this.position.x + iconDimension + 0.1
		});

		// connection
		this.connection.createAtCoordinates(this.position.x, this.position.y + this.headlineHeight);

		// seasons
		const {seasons} = this.props;
		if (seasons) {
			const bulletDiameter = 0.1;
			const bulletSpacing = 0.1;
			const priceWidth = 1;

			// used for alignment of prices
			const longestTitleInch = Math.max(
				...seasons.map(season => season.title).map(title => Math.max(0.8, title.length * 0.06))
			);

			seasons.forEach((season, index) => {
				let seasonColor = colors.defaultSeason;
				const y =
					this.position.y +
					this.headlineHeight +
					this.connection.getDimensions().h +
					this.seasonMargin +
					this.seasonTextHeight * index;

				if (season.type === SeasonType.HighSeason) seasonColor = colors.highSeason;
				if (season.type === SeasonType.LowSeason) seasonColor = colors.lowSeason;

				if (seasons.length > 1) {
					// bullet
					this.slide.addShape('roundRect', {
						x: this.position.x,
						y: y + this.seasonTextHeight / 2 - bulletDiameter / 2 + 0.01,
						w: bulletDiameter,
						h: bulletDiameter,
						rectRadius: bulletDiameter / 2,
						fill: { color: seasonColor }
					});

					// season title
					this.slide.addText(season.title, {
						...copy,
						...textDefaults,
						h: this.seasonTextHeight,
						x: this.position.x + bulletDiameter + bulletSpacing,
						y,
						w: longestTitleInch
					});
				}

				// price
				const priceX =
					seasons.length > 1 ? this.position.x + bulletDiameter + bulletSpacing + longestTitleInch : this.position.x;

				this.slide.addText(global.formatPriceWithCurrency(Number(this.getPriceForSeason(season))), {
					...copyBold,
					...textDefaults,
					h: this.seasonTextHeight,
					x: priceX,
					y,
					w: priceWidth
				});
			});
		}
	}

	protected getHeight(): number {
		let height = this.headlineHeight;
		height += this.connection.getDimensions().h;
		height += this.seasonMargin;
		height += this.props.seasons ? this.props.seasons.length * this.seasonTextHeight : 0;
		return height;
	}

	// TODO: share this logic, at the moment it is copied more or less from present view component ConnectingTicket.vue
	protected getPriceForSeason(season: ISeason | ISeasonWithPrice): number | undefined {
		if ((season as ISeasonWithPrice).price) {
			return (season as ISeasonWithPrice).price;
		}

		if (this.props.connection.priceGroups && this.props.connection.priceGroups.length) {
			switch (season.type) {
				case SeasonType.DefaultSeason:
					return this.props.connection.priceGroups[0].prices[0].defaultSeason;
				case SeasonType.HighSeason:
					return this.props.connection.priceGroups[0].prices[0].highSeason;
				case SeasonType.LowSeason:
					return this.props.connection.priceGroups[0].prices[0].lowSeason;
				default:
			}
		}

		return undefined;
	}
}
